import React, { useEffect, useRef, useState } from "react";
import { isEmpty } from "lodash";
import classNames from "classnames";
import { Collapse } from "@mui/material";

import Field from "components/Field";
import Checkbox from "components/Input/Checkbox";
import NonH2kParamTooltip from "components/NonH2kParamTooltip";
import InfoTooltip from "components/InfoTooltip";
import { ZoneErrorTooltip, ZoneWarningTooltip } from "../../ZoneTooltips";

import { useStateSelector } from "utils/field";
import {
    calculateCoveredPerimeter,
    componentHasWall,
    getComponentsInRoom,
} from "utils/singleZoneCooling";
import {
    convertArea,
    convertLength,
    formatArea,
} from "utils/displayMeasurements";
import { unitLabels } from "utils/fields";

import ArrowIcon from "assets/images/icons/arrow-down.svg";
import WallIcon from "assets/images/components/Wall.svg";
import ExpFloorIcon from "assets/images/components/ExpFloor.svg";
import CeilingIcon from "assets/images/components/Ceiling.svg";
import BasementIcon from "assets/images/components/Foundation-Basement.svg";
import CrawlspaceIcon from "assets/images/components/Foundation-Crawlspace.svg";
import SlabIcon from "assets/images/components/Foundation-Slab.svg";
import WindowIcon from "assets/images/components/Window.svg";
import DoorIcon from "assets/images/components/Door.svg";
import FloorHeaderIcon from "assets/images/components/FloorHeader.svg";

import classes from "./styles.module.scss";

const COMPONENT_ICONS = {
    wall: WallIcon,
    expFloor: ExpFloorIcon,
    ceiling: CeilingIcon,
    basement: BasementIcon,
    crawlspace: CrawlspaceIcon,
    slab: SlabIcon,
    window: WindowIcon,
    door: DoorIcon,
    floorHeader: FloorHeaderIcon,
};

const StoreyRoom = ({
    accessor,
    change,
    allMainComponents = {},
    displayUnits = {
        areaDisplayUnits: "m2",
        perimeterDisplayUnits: "m",
    },
    roomId = "",
    roomData: {
        name: roomLabel = "",
        area: floorArea = 0,
    },
    className,
}) => {
    const {
        selectedRooms = [],
        selectedWalls = [],
        warnings: {
            missingInteriorDoor = false,
            roomsWithIntWalls = [],
        } = {},
    } = useStateSelector(`${accessor}`) || {};

    const [expanded, setExpanded] = useState(false);

    const { areaDisplayUnits, perimeterDisplayUnits } = displayUnits;
    const roomIsSelected = selectedRooms.includes(roomId);
    const wallPerimeter = calculateCoveredPerimeter(allMainComponents, selectedWalls, roomIsSelected);

    return (
        <div
            className={classNames(classes.roomContainer, {
                [classes.isExpanded]: expanded,
                [classes.hasWarning]: missingInteriorDoor && roomsWithIntWalls.includes(roomId),
            })}
        >
            <div className={classNames(classes.roomHeader, classes.tableRow, { [className]: className })} onClick={() => setExpanded(!expanded)}>
                <div className={classNames(classes.tableCell, classes.roomLabelContainer)}>
                    <Field
                        component={Checkbox}
                        hideLabel={true}
                        className={classes.roomCheckbox}
                        input={{
                            value: roomIsSelected,
                            onChange: (value) => {
                                change(
                                    `${accessor}.selectedRooms`,
                                    value ? [...selectedRooms, roomId] : selectedRooms.filter((id) => id !== roomId)
                                );

                                const wallsInRoom = Object.entries(allMainComponents)
                                    .filter(([, componentData]) => componentData.type === "wall")
                                    .map(([componentKey]) => componentKey);
                                
                                if (value) {
                                    // Add all walls when room is selected
                                    change(`${accessor}.selectedWalls`, [...selectedWalls, ...wallsInRoom]);
                                } else {
                                    // Remove all walls when room is deselected
                                    change(`${accessor}.selectedWalls`, selectedWalls.filter((key) => !allComponentsInRoom[key]));
                                }
                            },
                        }}
                    />
                    {roomLabel}

                    {missingInteriorDoor && roomsWithIntWalls.includes(roomId) && (
                        <ZoneWarningTooltip
                            id={`${roomId}`}
                            warning={"missingInteriorDoor"}
                        />
                    )}
                </div>
                <div className={classNames(classes.tableCell, { [classes.fadeCell]: !roomIsSelected })}>
                    {convertArea(floorArea, areaDisplayUnits)}
                </div>
                <div className={classNames(classes.tableCell, { [classes.fadeCell]: !roomIsSelected })}>
                    {convertLength(wallPerimeter, perimeterDisplayUnits)}
                </div>
                <div className={classNames(classes.tableCell, classes.expandButton)}>
                    <img src={ArrowIcon} alt="expand" />
                </div>
            </div>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <ComponentList
                    accessor={accessor}
                    change={change}
                    allMainComponents={allMainComponents}
                    roomIsSelected={roomIsSelected}
                    selectedWalls={selectedWalls}
                    displayUnits={displayUnits}
                />
            </Collapse>
        </div>
    );
};

const ComponentList = ({
    accessor,
    change,
    allMainComponents = {},
    roomIsSelected = false,
    selectedWalls = [],
    displayUnits = {
        perimeterDisplayUnits: "m",
    },
}) => {
    return (
        <div className={classNames(classes.roomBody)}>
            {isEmpty(allMainComponents) && <div className={classes.emptyRoomNotice}>No components to display.</div>}
            {Object.entries(allMainComponents).map(([componentKey, componentData]) => {
                const subComponentList = Object.entries(componentData.subcomponents || {}).reduce(
                    (acc, [componentType, componentList]) => [
                        ...acc,
                        ...Object.entries(componentList).reduce(
                            (innerAcc, [subComponentKey, subComponentData]) => [
                                ...innerAcc,
                                [
                                    subComponentKey,
                                    {
                                        ...subComponentData,
                                        type: componentType,
                                    },
                                ],
                            ],
                            []
                        ),
                    ],
                    []
                );

                return (
                    <React.Fragment key={componentKey}>
                        <ComponentRow
                            accessor={accessor}
                            change={change}
                            componentKey={componentKey}
                            componentData={componentData}
                            roomIsSelected={roomIsSelected}
                            selectedWalls={selectedWalls}
                            displayUnits={displayUnits}
                        />
                        {!isEmpty(subComponentList) && (
                            <SubComponentsList
                                accessor={accessor}
                                change={change}
                                subComponentList={subComponentList}
                                roomIsSelected={roomIsSelected}
                                selectedWalls={selectedWalls}
                                displayUnits={displayUnits}
                                isInterior={componentData.isInterior}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

const SubComponentsList = ({
    accessor,
    change,
    subComponentList = [],
    roomIsSelected = false,
    selectedWalls = [],
    displayUnits = {
        perimeterDisplayUnits: "m",
    },
    isInterior = false,
}) => {
    return (
        <div className={classes.subComponentList}>
            {subComponentList.map(([subComponentKey, subComponentData]) => {
                const windows = subComponentData.subcomponents?.window || {};

                return (
                    <div key={subComponentKey} className={classes.isSubComponent}>
                        <ComponentRow
                            accessor={accessor}
                            change={change}
                            componentKey={subComponentKey}
                            componentData={{...subComponentData, isInterior }}
                            isSubComponent={true}
                            roomIsSelected={roomIsSelected}
                            selectedWalls={selectedWalls}
                            displayUnits={displayUnits}
                        />
                        {!isEmpty(windows) && (
                            <div className={classes.doorWindowList}>
                                {Object.entries(windows).map(([doorWindowKey, doorWindowData]) => (
                                    <div key={`${subComponentKey}-${doorWindowKey}`} className={classes.isDoorWindow}>
                                        <ComponentRow
                                            accessor={accessor}
                                            change={change}
                                            componentKey={doorWindowKey}
                                            componentData={{ ...doorWindowData, type: "window", isInterior }}
                                            isSubComponent={true}
                                            roomIsSelected={roomIsSelected}
                                            selectedWalls={selectedWalls}
                                            displayUnits={displayUnits}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const ComponentRow = ({
    accessor,
    change,
    componentKey = "",
    componentData = {},
    roomIsSelected = false,
    isSubComponent = false,
    selectedWalls = [],
    displayUnits = {
        perimeterDisplayUnits: "m",
    },
}) => {
    const {
        type = "",
        label = "",
        measurements: { perimeter = 0 } = {},
        exposedSurfacePerimeter = 0,
        isInterior = false,
    } = componentData;

    const { perimeterDisplayUnits } = displayUnits;

    const hasWall = componentHasWall(type) // basement, crawlspace, wall
    const isWall = type === "wall";

    const perimeterToUse = hasWall ? perimeter || exposedSurfacePerimeter : 0;
    const convertedPerimeter =
        (perimeterToUse > 0 && !isSubComponent)
            ? convertLength(perimeterToUse, perimeterDisplayUnits)
            : null;

    return (
        <div className={classNames(classes.componentRow, classes.tableRow, { [classes.interiorComponent]: isInterior })}>
            <div className={classNames(classes.tableCell, classes.componentInfo)}>
                <div className={classes.componentTitleContainer}>
                    <div className={classes.componentLabel}>
                        {!isSubComponent && (
                            <Field
                                component={Checkbox}
                                hideLabel={true}
                                className={classes.componentCheckbox}
                                disabled={!hasWall || !roomIsSelected || !isInterior}
                                input={{
                                    value: (!isWall && roomIsSelected) || selectedWalls.includes(componentKey),
                                    onChange: (value) => {
                                        if (!isWall) return;

                                        change(
                                            `${accessor}.selectedWalls`,
                                            value
                                                ? [...selectedWalls, componentKey]
                                                : selectedWalls.filter((key) => key !== componentKey)
                                        );
                                    },
                                }}
                            />
                        )}
                        <img src={COMPONENT_ICONS[type] || ""} alt={`${type}`} />
                        <p>{label}</p>
                        {isInterior && !isSubComponent && (
                            <NonH2kParamTooltip
                                className={classes.nonH2kParamTooltip}
                                nonH2kParam={"interiorComponent"}
                            />
                        )}
                    </div>
                </div>
            </div>
            <div className={classes.tableCell}>
                <p>&mdash;</p>
            </div>
            <div className={classes.tableCell}>{convertedPerimeter || <p>&mdash;</p>}</div>
            <div className={classes.tableCell}></div>
        </div>
    );
};

const ZoneRoomTable = ({
    change,
    accessor,
    roomsInStorey = [],
    componentsInStorey = {},
    intComponentsInStorey = {},
    minPerimeterRequired = 0,
    minAreaRequired = 0,
    displayUnits = {
        areaDisplayUnits: "m2",
        perimeterDisplayUnits: "m",
    },
    inZoneAccordion = false,
}) => {
    const { selectedStorey = "", selectedRooms = [], selectedWalls = [], measurements = {}, warnings = {}, requirements = {} } = useStateSelector(`${accessor}`) || {};
    const { totalFloorArea = 0, totalWallPerimeter = 0 } = measurements || {};
    const { hasMinArea = false, hasWallOutlet = false, hasOtherElectrical = false, hasVentilation = false, ventilationType = null } = requirements || {};

    const { areaDisplayUnits, perimeterDisplayUnits } = displayUnits;

    const previousStoreyRef = useRef(selectedStorey);

    const { allComponentsInRooms = {}, componentsByRooms = {}, intComponentsByRooms = {} } = roomsInStorey.reduce((acc, [roomId, _]) => {
        const { componentsInRoom = {}, intComponentsInRoom = {} } = getComponentsInRoom(componentsInStorey, intComponentsInStorey, roomId);

        return {
            allComponentsInRooms: {
                ...acc.allComponentsInRooms,
                [roomId]: { ...componentsInRoom, ...intComponentsInRoom }
            },
            componentsByRooms: {
                ...acc.componentsByRooms,
                [roomId]: componentsInRoom || {}
            },
            intComponentsByRooms: {
                ...acc.intComponentsByRooms,
                [roomId]: intComponentsInRoom || {}
            },
        }
    }, { allComponentsInRooms: {}, componentsByRooms: {}, intComponentsByRooms: {} });

    // Update the total zone summary when the rooms and walls to include are updated
    useEffect(() => {
        let totalArea = 0;
        let totalPerimeter = 0;

        selectedRooms.forEach(roomId => {
            const roomArea = Object.fromEntries(roomsInStorey)?.[roomId]?.area || 0;

            totalArea += parseFloat(roomArea);
            totalPerimeter += calculateCoveredPerimeter(allComponentsInRooms[roomId], selectedWalls);
        });

        change(`${accessor}.measurements`, {
            totalFloorArea: totalArea,
            totalWallPerimeter: totalPerimeter,
        });
    }, [selectedRooms, selectedWalls]);

    // Warning Checks for the cooling zone
    useEffect(() => {
        const allWarnings = {};

        // Check whether the zone is fully enclosed
        const minReached = minPerimeterRequired <= totalWallPerimeter;

        change(`${accessor}.isFullyEnclosed`, minPerimeterRequired > 0 && minReached);

        if (minPerimeterRequired > 0 && !minReached) {
            allWarnings.notFullyEnclosed = true;
        }

        // Check whether there is at least one interior wall in the cooling zone that has a door
        const { roomsWithIntWalls, intDoorFound } = selectedRooms.reduce((acc, roomId) => {
            const intWallsInRoom = Object.entries(intComponentsByRooms[roomId] || {})
                .filter(([componentKey, componentData]) => componentData.type === "wall" && selectedWalls.includes(componentKey));

            if (!isEmpty(intWallsInRoom)) {
                return {
                    roomsWithIntWalls: [...acc.roomsWithIntWalls, roomId],
                    intDoorFound: acc.intDoorFound || intWallsInRoom.some(([, componentData]) => Object.keys(componentData.subcomponents || {}).includes("door")),
                };
            }
            return acc;
        }, { roomsWithIntWalls: [], intDoorFound: false });

        if (!intDoorFound && !isEmpty(roomsWithIntWalls)) {
            allWarnings.missingInteriorDoor = true;
            allWarnings.roomsWithIntWalls = roomsWithIntWalls;
        }

        // Update warnings
        change(`${accessor}.warnings`, allWarnings);
    }, [minPerimeterRequired, totalWallPerimeter, selectedRooms, selectedWalls]);

    // Reset rooms and walls only when the storey field is updated, not on remount
    useEffect(() => {
        if (previousStoreyRef.current === selectedStorey || !selectedStorey) {
            return;
        }

        previousStoreyRef.current = selectedStorey;

        change(`${accessor}.selectedRooms`, []);
        change(`${accessor}.selectedWalls`, []);
    }, [selectedStorey]);

    return (
        <div className={classNames(classes.tableContainer)}>
            <div className={classes.tableContent}>
                <div className={classNames(classes.stickyHeader, { [classes.adjustTop]: inZoneAccordion })}>
                    <h3 className={classes.sectionTitle}>
                        Cooling Zone Rooms
                        <InfoTooltip
                            info="For accuracy, expand a selected room and deselect any interior walls that do not enclose the cooling zone from the outside or adjacent spaces."
                        />
                    </h3>
                    <div className={classNames(classes.roomSelectTableHeader, classes.tableRow)}>
                        <div className={classes.tableCell}>Room Name</div>
                        <div className={classes.tableCell}>Floor Area ({unitLabels(areaDisplayUnits)})</div>
                        <div className={classes.tableCell}>Wall Perimeter ({unitLabels(perimeterDisplayUnits)})</div>
                        <div className={classes.tableCell}></div>
                    </div>
                </div>
                <div className={classes.roomSelectTableBody}>
                    {isEmpty(roomsInStorey) && (
                        <div className={classes.emptyStoreyList}>
                            <p> Please select a storey with rooms to continue </p>
                        </div>
                    )}
                    {roomsInStorey.map(([roomId, roomData]) => {
                        return (
                            <StoreyRoom
                                key={roomId}
                                accessor={accessor}
                                change={change}
                                allMainComponents={allComponentsInRooms?.[roomId]}
                                displayUnits={displayUnits}
                                roomId={roomId}
                                roomData={roomData}
                                className={inZoneAccordion ? classes.adjustTop : ""}
                            />
                        );
                    })}
                </div>
            </div>
            <div className={classes.tableFooter}>
                {!isEmpty(roomsInStorey) && (
                    <div
                        className={classNames(classes.tableFooter, classes.tableRow, {
                            [classes.hasWarning]: warnings?.notFullyEnclosed,
                            [classes.hasError]: !hasMinArea,
                        })}
                    >
                        <div className={classes.tableCell}>Cooling Zone Total</div>
                        <div className={classes.tableCell}>
                            {!hasMinArea && (
                                <ZoneErrorTooltip
                                    id={`totalFloorArea`}
                                    error={"minAreaRequired"}
                                    formattedValue={formatArea(minAreaRequired, areaDisplayUnits)}
                                />
                            )}
                            <span>
                                {convertArea(totalFloorArea, areaDisplayUnits)}
                            </span>
                        </div>
                        <div className={classes.tableCell}>
                            {warnings?.notFullyEnclosed && (
                                <ZoneWarningTooltip
                                    id={`totalWallPerimeter`}
                                    warning={"notFullyEnclosed"}
                                />
                            )}
                            <span>
                                {convertLength(totalWallPerimeter, perimeterDisplayUnits)}
                            </span>
                        </div>
                        <div className={classes.tableCell}></div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ZoneRoomTable;
